import { useCallback, useEffect, useState } from "react";
import { Thermometer, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { currentMonth, todayISO, fmtDay } from "@/utils/haccp";

export default function HaccpTemperatures() {
  const { authAxios } = useAuth();
  const [month, setMonth] = useState(currentMonth());
  const [items, setItems] = useState([]);
  const [points, setPoints] = useState([]);
  const [form, setForm] = useState({ point_id: "", slot: "mattina", temp: "", date: todayISO(), note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([
        authAxios.get(`/haccp/temperatures?month=${month}`),
        authAxios.get("/haccp/points"),
      ]);
      setItems(t.data.items || []);
      setPoints(p.data.items || []);
      setForm((f) => ({ ...f, point_id: f.point_id || p.data.items?.[0]?.id || "" }));
    } catch {
      toast.error("Errore caricamento rilevazioni");
    }
  }, [authAxios, month]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const n = parseFloat(String(form.temp).replace(",", "."));
    if (!form.point_id) { toast.error("Seleziona un punto di misura"); return; }
    if (Number.isNaN(n)) { toast.error("Temperatura non valida"); return; }
    setSaving(true);
    try {
      const r = await authAxios.post("/haccp/temperatures", { ...form, temp: n });
      toast[r.data.ok ? "success" : "warning"](r.data.ok ? "Rilevazione salvata" : "Rilevazione salvata: FUORI RANGE");
      setForm((f) => ({ ...f, temp: "", note: "" }));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa rilevazione?")) return;
    try {
      await authAxios.delete(`/haccp/temperatures/${id}`);
      await load();
    } catch { toast.error("Errore eliminazione"); }
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="haccp-temperatures-page">
      <main className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto pb-16">
        <PageHeader title="Temperature HACCP" />

        <Card className="bg-[#18181b] border-sky-500/20 mb-5">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Thermometer className="w-4 h-4 text-sky-400" />
              <h3 className="text-white text-base font-semibold">Nuova rilevazione</h3>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select
                data-testid="temp-point-select"
                value={form.point_id}
                onChange={(e) => setForm({ ...form, point_id: e.target.value })}
                className="h-10 rounded-md bg-[#0f0f11] border border-white/10 text-white text-sm px-2"
              >
                {points.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.min_temp}÷{p.max_temp}°C)</option>
                ))}
              </select>
              <select
                data-testid="temp-slot-select"
                value={form.slot}
                onChange={(e) => setForm({ ...form, slot: e.target.value })}
                className="h-10 rounded-md bg-[#0f0f11] border border-white/10 text-white text-sm px-2"
              >
                <option value="mattina">Mattina</option>
                <option value="pomeriggio">Pomeriggio</option>
              </select>
              <Input
                type="date"
                data-testid="temp-date-input"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="h-10 bg-[#0f0f11] border-white/10 text-white"
              />
              <Input
                data-testid="temp-value-input"
                value={form.temp}
                inputMode="decimal"
                placeholder="Temperatura °C"
                onChange={(e) => setForm({ ...form, temp: e.target.value })}
                className="h-10 bg-[#0f0f11] border-white/10 text-white"
              />
            </div>
            <Input
              data-testid="temp-note-input"
              value={form.note}
              placeholder="Note (opzionale)"
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="h-10 bg-[#0f0f11] border-white/10 text-white"
            />
            <Button onClick={save} disabled={saving} data-testid="temp-submit-btn" className="w-full h-10 bg-amber-500 hover:bg-amber-400 text-black">
              Registra
            </Button>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between gap-2 mb-3">
          <h3 className="text-white text-base font-semibold">Storico</h3>
          <Input
            type="month"
            data-testid="temp-month-filter"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-9 w-40 bg-[#0f0f11] border-white/10 text-white"
          />
        </div>

        <div className="space-y-2" data-testid="temp-history-list">
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#18181b] border border-white/10">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">
                  {fmtDay(it.date)} · <span className="capitalize text-zinc-400">{it.slot}</span> · {it.point_name}
                </p>
                <p className="text-[11px] text-zinc-500 truncate">
                  Firma: {it.signed_by?.name || "—"}{it.note ? ` · ${it.note}` : ""}
                </p>
              </div>
              <span className={`text-sm font-semibold ${it.ok ? "text-emerald-400" : "text-red-400"}`}>{it.temp}°C</span>
              <Button variant="ghost" size="sm" onClick={() => remove(it.id)} data-testid={`temp-delete-${it.id}`} className="text-zinc-500 hover:text-red-400">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {!items.length && <p className="text-zinc-500 text-sm">Nessuna rilevazione nel mese selezionato.</p>}
        </div>
      </main>
    </div>
  );
}
